import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronRight, DownloadCloud, KeyRound, QrCode, Server } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { licensingApi, type LicensingStationRecord } from '@/api/licensing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useClientContext } from '../client/ClientContext';
import { GuestDownloadApprovalPanel } from './GuestDownloadApprovalPanel';
import { isMailArchiverApp, MailArchiverIcon } from './MailArchiverIcon';
import { ReleaseDownloadGrantsPanel } from './ReleaseDownloadGrantsPanel';
import { getStationListState } from './licensingViewModel';

function formatDateTime(value?: string): string {
  if (!value) return 'нет данных';
  return new Date(value).toLocaleString('ru-RU');
}

function stationTone(station: LicensingStationRecord): 'visit' | 'deadline' | 'outline' {
  if (station.status === 'HEALTHY' && !station.outdated) return 'visit';
  if (station.status === 'SUSPENDED' || station.status === 'DISABLED') return 'deadline';
  return 'outline';
}

export function ClientLicensingTab() {
  const { clientId, detail } = useClientContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('licensingSection') === 'installers' ? 'installers' : 'licenses';
  const query = useQuery({
    queryKey: ['licensing', 'client-stations', clientId],
    queryFn: () => licensingApi.listClientStations(clientId),
    enabled: activeSection === 'licenses',
  });
  const listState = getStationListState({
    isLoading: query.isLoading,
    isError: query.isError,
    itemCount: query.data?.items.length ?? 0,
  });
  const setSection = (section: 'licenses' | 'installers') => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'licensing');
    next.set('licensingSection', section);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">Лицензии ПО</h2>
          <p className="text-sm text-muted-foreground truncate">
            {detail?.client.name ?? `Клиент ${clientId}`}
          </p>
        </div>
        {activeSection === 'licenses' && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(`/more/licensing?clientId=${clientId}`, {
                state: { clientName: detail?.client.name },
              })
            }
          >
            <QrCode className="h-4 w-4" />
            Привязать
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-lg border bg-card p-1">
        <Button
          type="button"
          variant={activeSection === 'licenses' ? 'default' : 'ghost'}
          className="h-10"
          onClick={() => setSection('licenses')}
        >
          <KeyRound className="h-4 w-4" />
          Лицензии
        </Button>
        <Button
          type="button"
          variant={activeSection === 'installers' ? 'default' : 'ghost'}
          className="h-10"
          onClick={() => setSection('installers')}
        >
          <DownloadCloud className="h-4 w-4" />
          Установщики
        </Button>
      </div>

      {activeSection === 'installers' && (
        <>
          <GuestDownloadApprovalPanel fixedClientId={clientId} clientName={detail?.client.name} />
          <ReleaseDownloadGrantsPanel clientId={clientId} clientName={detail?.client.name} />
        </>
      )}

      {activeSection === 'licenses' && listState === 'loading' && (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {activeSection === 'licenses' && listState === 'denied' && (
        <DeniedState message="Станции недоступны или у вас нет доступа к этому клиенту." />
      )}

      {activeSection === 'licenses' && listState === 'empty' && (
        <div className="rounded-lg border bg-card p-4 text-center">
          <KeyRound className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">Станций пока нет</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Зарегистрируйте установку через QR в приложении клиента.
          </p>
        </div>
      )}

      {activeSection === 'licenses' && listState === 'ready' && query.data && (
        <div className="space-y-2">
          {query.data.items.map((station) => (
            <StationCard
              key={station.id}
              station={station}
              onOpen={() =>
                navigate(`/more/licensing/stations/${station.id}`, {
                  state: { licensingBackTo: `/visits/client/${clientId}?tab=licensing&licensingSection=licenses` },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StationCard({ station, onOpen }: { station: LicensingStationRecord; onOpen: () => void }) {
  return (
    <button
      type="button"
      data-testid="client-license-station-card"
      data-station-id={station.id}
      className="w-full rounded-lg border bg-card p-3 text-left active:scale-[0.99]"
      onClick={onOpen}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          {isMailArchiverApp(station.appId) ? (
            <MailArchiverIcon className="h-5 w-5 [image-rendering:pixelated]" />
          ) : (
            <Server className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm font-medium">
              {station.hostname || station.installationId}
            </p>
            <Badge variant={stationTone(station)} className="shrink-0">
              {station.status}
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{station.appId}</span>
            <span>{station.currentVersion || 'без версии'}</span>
            <span>{station.os}/{station.arch}</span>
          </div>
          {station.policy?.notes && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{station.policy.notes}</p>
          )}
          <p className={cn('mt-1 text-xs', station.outdated ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}>
            {station.outdated ? 'Версия ниже минимальной' : `Последний контакт: ${formatDateTime(station.lastSeenAt)}`}
          </p>
        </div>
        <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </button>
  );
}

function DeniedState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Нет доступа</p>
          <p className="mt-1 text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    </div>
  );
}
